import {
  Timestamp,
} from 'firebase/firestore'
import { getDebugHelperAssignedItems, isNoSignInPreviewActive } from '../../debug'
import type { ChecklistItem, ChecklistWorkflowStatus } from '../../types/schema'
import {
  isFieldVisibleToHelper,
  mergeHelperVisibilityMask,
  type HelperVisibilityMask,
} from './visibilityMask'
import { getChecklistDependencyBlockState } from '../../utils/checklistBlockedTodoPolicy'
import { getWorkspaceTodayIso, getWorkspaceWeekKey } from '../workspaceDayService'
import { subscribeChecklistItemsView } from '../checklistReader'
import { getHelperSnapshot, subscribeHelperStore } from './helperStore'
import {
  isChecklistAssignedToUser,
  ACTIVE_WORKFLOW_STATUSES,
} from '../checklistVisibility'

/**
 * Phase 3.C — Field → ChecklistItem-key map used by the service-layer redactor.
 *
 * Keys that are mask-controlled: when the effective mask hides the field, the
 * service strips the listed item properties before passing the item to the
 * caller. Identity / safety fields (id, workspaceId, parentContainerId,
 * assigneeId, isCompleted, updatedAt, recordVersion, workflowStatus,
 * helperVisibilityMask itself) are NEVER masked because the UI needs them to
 * route, render skeletons, and detect conflicts.
 */

const MASK_FIELD_MAP: Partial<Record<keyof HelperVisibilityMask, ReadonlyArray<keyof ChecklistItem>>> = {
  description: ['description'],
  completionCriteria: ['completionCriteria'],
  priorityChips: ['priority'],
  urgencyStars: ['urgencyStars'],
  importanceStars: ['importanceStars'],
  complexityStars: ['complexityStars'],
  weightage: ['weightage'],
  scheduleWindow: ['scheduleStartDate', 'scheduleEndDate', 'searchDateBuckets'],
  timeWindow: ['hasTimeWindow', 'scheduleTimeFrom', 'scheduleTimeTo'],
  estimatedHours: ['estimatedHours'],
  actualCost: ['actualCost'],
  currencyCode: ['currencyCode'],
  requiresProofFile: ['requiresProofFile', 'proofFileUrl', 'proofFileName', 'proofFileStoragePath'],
  requiresLocationPin: ['requiresLocationPin'],
  targetCoordinates: ['targetCoordinates'],
  dependencies: ['blockedByNodeIds', 'isBlockerForNodeIds', 'dependencyNodeIds', 'prerequisites'],
  commentsCount: ['commentsCount'],
}

const applyServiceSideRedaction = (
  item: ChecklistItem,
  effectiveMask: HelperVisibilityMask,
): ChecklistItem => {
  const redacted = { ...item } as Record<string, unknown>
  for (const [field, propertyKeys] of Object.entries(MASK_FIELD_MAP) as Array<[
    keyof HelperVisibilityMask,
    ReadonlyArray<keyof ChecklistItem>,
  ]>) {
    if (isFieldVisibleToHelper(effectiveMask, field)) continue
    for (const property of propertyKeys) {
      delete redacted[property as string]
    }
  }
  return redacted as unknown as ChecklistItem
}

export type WorkspaceMaskLookup = (workspaceId: string) => HelperVisibilityMask | undefined

// Re-export from the central visibility module so both helper and member
// feeds share one definition. Kept as a const for legacy imports.
const ACTIVE_STATUSES = ACTIVE_WORKFLOW_STATUSES

const toIso = (value: unknown): string | null => {
  if (!value) return null
  if (value instanceof Timestamp) return value.toDate().toISOString()
  if (typeof value === 'string') return value
  if (typeof value === 'object' && 'toDate' in (value as Record<string, unknown>)) {
    const fn = (value as { toDate?: () => Date }).toDate
    if (typeof fn === 'function') return fn.call(value).toISOString()
  }
  return null
}

const inferStatus = (raw: unknown, isCompleted: boolean): ChecklistWorkflowStatus | null => {
  if (typeof raw === 'string' && [
    'CREATED', 'ASSIGNED', 'ASSESSING', 'ACCEPTED', 'ONGOING', 'COMPLETED', 'NOT_REQUIRED', 'POSTPONED',
  ].includes(raw)) {
    return raw as ChecklistWorkflowStatus
  }
  return isCompleted ? 'COMPLETED' : 'ASSIGNED'
}

export type HelperAssignedItem = ChecklistItem & {
  /** Stable normalized status used by the feed filter. */
  effectiveStatus: ChecklistWorkflowStatus
  /** ISO string for sort/conflict comparisons. */
  updatedAtIso: string | null
  dependencyBlocked?: boolean
  dependencyBlockReasons?: string[]
}

const normalize = (id: string, data: Record<string, unknown>): HelperAssignedItem => {
  const isCompleted = data.isCompleted === true
  const inferred = inferStatus(data.workflowStatus, isCompleted)
  const hasAssignee = typeof data.assigneeId === 'string' && data.assigneeId.trim().length > 0
  const status = hasAssignee && inferred === 'CREATED' ? 'ASSIGNED' : inferred
  const item = { ...data, id } as unknown as ChecklistItem
  return {
    ...item,
    effectiveStatus: status ?? 'ASSIGNED',
    updatedAtIso: toIso(data.updatedAt),
  }
}

const normalizeItem = (item: ChecklistItem): HelperAssignedItem =>
  normalize(item.id, item as unknown as Record<string, unknown>)

export const projectHelperAssignedItems = (
  rawItems: readonly ChecklistItem[],
  input: {
    uid: string
    workspaceMaskLookup?: WorkspaceMaskLookup
    includeDependencyState?: boolean
    workspaceTimeZoneLookup?: (workspaceId: string) => string | undefined
    now?: Date
  },
): HelperAssignedItem[] => {
  const normalizedItems = rawItems.map((item) => normalizeItem(item))
  const itemById = new Map(normalizedItems.map((item) => [item.id, item as ChecklistItem]))
  return normalizedItems
    .filter((item) => isChecklistAssignedToUser(item, input.uid))
    .map((item) => {
      let periodKey: string | undefined
      if (input.includeDependencyState) {
        const tz = input.workspaceTimeZoneLookup?.(item.workspaceId)
        const nowDate = input.now ?? new Date()
        periodKey = item.checklistType === 'WEEKLY' ? getWorkspaceWeekKey(tz, nowDate) : getWorkspaceTodayIso(tz, nowDate)
      }
      const dependencyState = input.includeDependencyState
        ? getChecklistDependencyBlockState(item, itemById, periodKey)
        : null
      const workspaceDefault = input.workspaceMaskLookup?.(item.workspaceId)
      const itemOverride = (item.helperVisibilityMask ?? null) as HelperVisibilityMask | null
      const effectiveMask = mergeHelperVisibilityMask(workspaceDefault, itemOverride)
      return {
        ...applyServiceSideRedaction(item, effectiveMask),
        ...(dependencyState ? {
          dependencyBlocked: dependencyState.isBlocked,
          dependencyBlockReasons: dependencyState.reasons.map((reason) => reason.title ?? reason.targetId ?? 'Dependency'),
        } : {}),
      } as HelperAssignedItem
    })
}

export const subscribeHelperAssignedItems = (
  uid: string,
  callback: (items: HelperAssignedItem[]) => void,
  workspaceMaskLookup?: WorkspaceMaskLookup,
  onError?: (err: unknown) => void,
): (() => void) => {
  if (isNoSignInPreviewActive()) {
    callback(getDebugHelperAssignedItems())
    return () => {}
  }
  const workspaceItems = new Map<string, HelperAssignedItem[]>()
  const workspaceUnsubs = new Map<string, () => void>()

  const emit = () => {
    callback(Array.from(workspaceItems.values()).flat())
  }

  const syncWorkspaceSubscriptions = (workspaceIds: readonly string[]) => {
    const nextIds = new Set(workspaceIds.filter((id) => typeof id === 'string' && id.trim().length > 0))

    for (const [workspaceId, unsubscribe] of workspaceUnsubs.entries()) {
      if (nextIds.has(workspaceId)) continue
      unsubscribe()
      workspaceUnsubs.delete(workspaceId)
      workspaceItems.delete(workspaceId)
    }

    for (const workspaceId of nextIds) {
      if (workspaceUnsubs.has(workspaceId)) continue
      const unsubscribe = subscribeChecklistItemsView(
        { workspaceId },
        (rawItems) => {
          workspaceItems.set(workspaceId, projectHelperAssignedItems(rawItems, {
            uid,
            workspaceMaskLookup,
            includeDependencyState: true,
          }))
          emit()
        },
        onError,
      )
      workspaceUnsubs.set(workspaceId, unsubscribe)
    }

    emit()
  }

  const storeUnsub = subscribeHelperStore((snapshot) => {
    syncWorkspaceSubscriptions(snapshot.workspaces.map((workspace) => workspace.id))
  })

  syncWorkspaceSubscriptions(getHelperSnapshot().workspaces.map((workspace) => workspace.id))

  return () => {
    storeUnsub()
    workspaceUnsubs.forEach((unsubscribe) => unsubscribe())
    workspaceUnsubs.clear()
    workspaceItems.clear()
  }
}

export const subscribeHelperAssignedItemsForWorkspace = (
  uid: string,
  workspaceId: string,
  callback: (items: HelperAssignedItem[]) => void,
  workspaceMaskLookup?: WorkspaceMaskLookup,
  onError?: (err: unknown) => void,
): (() => void) => {
  if (isNoSignInPreviewActive()) {
    callback(getDebugHelperAssignedItems().filter((item) => item.workspaceId === workspaceId))
    return () => {}
  }
  return subscribeChecklistItemsView(
    { workspaceId },
    (rawItems) => {
      callback(projectHelperAssignedItems(rawItems, {
        uid,
        workspaceMaskLookup,
        includeDependencyState: true,
      }))
    },
    onError,
  )
}

export const isHelperActiveStatus = (status: ChecklistWorkflowStatus): boolean =>
  ACTIVE_STATUSES.has(status)
